/**
 * session-expiry.ts — Hết phiên khi đang dùng hệ thống: thay vì văng về /login, hiện hộp "Đăng nhập lại" ngay trên
 * trang hiện tại (SessionExpiredModal trong AppLayout). Trong lúc chờ, mọi request API (trừ các endpoint đăng nhập)
 * bị TẠM GIỮ ở interceptor và tự chạy tiếp sau khi đăng nhập lại cùng tài khoản.
 *
 * Không có "host" (hộp chưa được gắn — vd. đang ở trang công khai / lúc khởi động) → quay về hành vi cũ (/login).
 * Module này KHÔNG import api-client/auth-store để tránh vòng phụ thuộc.
 */
import { create } from 'zustand'
import { i18n } from '@/lib/i18n'

// ── Trạng thái thuần (dễ kiểm thử) ─────────────────────────────────
export interface SessionModalState {
  /** `expired` = đang hiện hộp đăng nhập lại, request bị giữ. */
  status: 'active' | 'expired'
  /** Số hộp đăng nhập lại đang được gắn trên màn hình (AppLayout). */
  hosts: number
}

export type SessionModalAction =
  | { type: 'host/add' }
  | { type: 'host/remove' }
  | { type: 'expire' }
  | { type: 'resume' }

export const INITIAL_SESSION_STATE: SessionModalState = { status: 'active', hosts: 0 }

export function sessionReducer(s: SessionModalState, a: SessionModalAction): SessionModalState {
  switch (a.type) {
    case 'host/add':
      return { ...s, hosts: s.hosts + 1 }
    case 'host/remove': {
      const hosts = Math.max(0, s.hosts - 1)
      // Không còn ai hiện hộp → không được giữ request mãi.
      return { hosts, status: hosts === 0 ? 'active' : s.status }
    }
    case 'expire':
      return s.hosts > 0 ? { ...s, status: 'expired' } : s
    case 'resume':
      return { ...s, status: 'active' }
  }
}

/** Sau khi đăng nhập lại: cùng người + cùng tổ chức → chạy tiếp; khác → tải lại trang để không trộn dữ liệu. */
export function decideAfterRelogin(
  prev: { id: string; orgId: string } | null | undefined,
  next: { id: string; orgId: string },
): 'resume' | 'reload' {
  if (!prev) return 'reload'
  return prev.id === next.id && prev.orgId === next.orgId ? 'resume' : 'reload'
}

// ── Đường dẫn / endpoint công khai ─────────────────────────────────
const PUBLIC_AUTH_PATHS = ['/login', '/register', '/signup', '/forgot-password']

/** Trang đăng nhập / đăng ký / quên mật khẩu (không cần phiên). */
export function isPublicAuthPath(pathname: string): boolean {
  return PUBLIC_AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/** Endpoint không cần phiên: 401 ở đây là lỗi nghiệp vụ, và không bị giữ khi đang chờ đăng nhập lại. */
export function isPublicAuthUrl(url: string): boolean {
  return /\/auth\/(login|refresh|register|google|password\/|signup\/)/.test(url)
}

// ── Thông báo khi bị đăng xuất hẳn (tổ chức bị khoá / hết hạn) ─────
export const LOGOUT_NOTICE_KEY = 'ecdp_logout_notice'

export function logoutNoticeFor(code: string | undefined, serverMessage?: string | null): string | null {
  if (code === 'ORG_SUSPENDED') return serverMessage || i18n.t('sharedMisc:sessionExpiry.orgSuspended')
  if (code === 'ORG_EXPIRED') return serverMessage || i18n.t('sharedMisc:sessionExpiry.orgExpired')
  return null
}

export function saveLogoutNotice(msg: string) {
  try {
    sessionStorage.setItem(LOGOUT_NOTICE_KEY, msg)
  } catch {
    /* bỏ qua */
  }
}

/** Đọc thông báo đăng xuất (không xoá — an toàn với StrictMode gọi hàm khởi tạo 2 lần). */
export function peekLogoutNotice(): string | null {
  try {
    return sessionStorage.getItem(LOGOUT_NOTICE_KEY)
  } catch {
    return null
  }
}

export function clearLogoutNotice() {
  try {
    sessionStorage.removeItem(LOGOUT_NOTICE_KEY)
  } catch {
    /* bỏ qua */
  }
}

// ── Store dùng chung ───────────────────────────────────────────────
interface SessionModalStore extends SessionModalState {
  dispatch: (a: SessionModalAction) => void
}

export const useSessionModal = create<SessionModalStore>((set, get) => ({
  ...INITIAL_SESSION_STATE,
  dispatch: (a) => {
    const before = get().status
    const next = sessionReducer({ status: before, hosts: get().hosts }, a)
    set(next)
    if (before === 'expired' && next.status === 'active') flushWaiters()
  },
}))

let waiters: Array<() => void> = []
function flushWaiters() {
  const w = waiters
  waiters = []
  w.forEach((fn) => fn())
}

export const isSessionExpired = () => useSessionModal.getState().status === 'expired'

/** Gắn hộp đăng nhập lại (gọi trong effect); trả hàm gỡ. */
export function registerSessionHost(): () => void {
  useSessionModal.getState().dispatch({ type: 'host/add' })
  return () => useSessionModal.getState().dispatch({ type: 'host/remove' })
}

/** Mở hộp đăng nhập lại. Trả `true` nếu có hộp xử lý (không cần chuyển trang). */
export function requestRelogin(): boolean {
  const { dispatch } = useSessionModal.getState()
  dispatch({ type: 'expire' })
  return isSessionExpired()
}

/** Đăng nhập lại thành công (cùng tài khoản) → thả các request đang giữ. */
export function resumeSession() {
  useSessionModal.getState().dispatch({ type: 'resume' })
}

/** Chờ tới khi phiên hoạt động lại (trả ngay nếu không hết phiên). */
export function waitForSession(): Promise<void> {
  if (!isSessionExpired()) return Promise.resolve()
  return new Promise((resolve) => waiters.push(resolve))
}
